import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { CareConciergeFormData, RequestorType } from './types';

type RequestorTypeSelectorProps = {
  register: UseFormRegister<CareConciergeFormData>;
  errors: FieldErrors<CareConciergeFormData>;
  selectedType?: RequestorType;
  showFinancialAdvisorOption?: boolean;
};

const ALL_OPTIONS: RequestorType[] = ['SELF', 'LOVED_ONE', 'FINANCIAL_ADVISOR'];

const RequestorTypeSelector: React.FC<RequestorTypeSelectorProps> = ({
  register,
  errors,
  selectedType,
  showFinancialAdvisorOption = true,
}) => {
  const visibleOptions = showFinancialAdvisorOption
    ? ALL_OPTIONS
    : ALL_OPTIONS.filter((t) => t !== 'FINANCIAL_ADVISOR');
  const getTypeLabel = (type: RequestorType) => {
    switch (type) {
      case 'SELF':
        return 'Myself';
      case 'LOVED_ONE':
        return 'A loved one';
      case 'FINANCIAL_ADVISOR':
        return 'A financial advisor submitting on behalf of a client';
    }
  };

  const getTypeDescription = (type: RequestorType) => {
    switch (type) {
      case 'SELF':
        return "I'm seeking care or support for myself";
      case 'LOVED_ONE':
        return "I'm seeking care or support for a family member or friend";
      case 'FINANCIAL_ADVISOR':
        return "I'm a financial advisor helping a client find care or support";
    }
  };

  // If a type is selected, show only the selected option
  if (selectedType) {
    return (
      <div>
        <label className="block text-base font-termina font-medium text-[#111827] mb-3">
          Submitting request as:
        </label>
        <div className="p-4 border-2 border-[#026857] bg-[#026857]/5 rounded-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-[#026857] flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <span className="block font-termina font-medium text-[#026857]">
                {getTypeLabel(selectedType)}
              </span>
              <span className="block text-sm text-[#4B5563] font-termina mt-1">
                {getTypeDescription(selectedType)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show filtered options when nothing is selected
  return (
    <div>
      <label className="block text-base font-termina font-medium text-[#111827] mb-3">
        Who is making this request? <span className="text-red-500">*</span>
      </label>
      <div className="space-y-3">
        {visibleOptions.map((type) => (
          <label
            key={type}
            className="flex items-start gap-3 p-4 border-2 border-[#E5E7EB] rounded-lg cursor-pointer hover:border-[#026857] hover:bg-[#026857]/5 transition-colors"
          >
            <input
              type="radio"
              value={type}
              {...register('requestorType', { required: 'Please select who is making this request' })}
              className="mt-1 w-4 h-4 text-[#026857] border-[#E5E7EB] focus:ring-[#026857]"
            />
            <div className="flex-1">
              <span className="block font-termina font-medium text-[#111827]">
                {getTypeLabel(type)}
              </span>
              <span className="block text-sm text-[#6B7280] font-termina mt-1">
                {getTypeDescription(type)}
              </span>
            </div>
          </label>
        ))}
      </div>
      {errors.requestorType && (
        <p className="mt-2 text-sm text-red-600 font-termina">{errors.requestorType.message}</p>
      )}
    </div>
  );
};

export default RequestorTypeSelector;
